import { TOOLS } from "../decisions/tools";
import { GUIDES, type GuideKey } from "../site/guides";

/**
 * Where AbiChat can take the user: the app's pages (app/router.tsx), each with what it's for (the
 * page's own subtitle, `pages.<key>.sub` in app-copy.json) and its page guide, if it has one. The
 * site-control tools accept only these keys and the Decisions tools' slugs, never a free-form URL.
 */
export type AppPage = {
  key: string;
  href: string;
  title: string;
  about: string;
  guide?: GuideKey;
};

export const APP_PAGES: AppPage[] = [
  {
    key: "home",
    href: "/app",
    title: "Home",
    about: "The plan at a glance, and the box to ask Abi a question.",
    guide: "home",
  },
  {
    key: "actions",
    href: "/app/actions",
    title: "Actions",
    about:
      "The short list, in the order Abi would do them. Open any one to see why it is on the list.",
    guide: "actions",
  },
  {
    key: "cash-flow",
    href: "/app/cash-flow",
    title: "Cash flow",
    about: "What you earn, which account it lands in, and what each account actually pays for.",
    guide: "cashflow",
  },
  {
    key: "budget",
    href: "/app/budget",
    title: "Budget",
    about:
      "What you normally spend, what you would rather spend, and how the two compare month to month.",
    guide: "budget",
  },
  {
    key: "assets",
    href: "/app/assets",
    title: "Assets",
    about: "What you hold, what you paid for it, and what it is worth now.",
    guide: "assets",
  },
  {
    key: "debt",
    href: "/app/debt",
    title: "Debt",
    about:
      "What you owe, what each payment actually buys, and what changes if you follow the plan.",
    guide: "debt",
  },
  {
    key: "net-worth",
    href: "/app/net-worth",
    title: "Net worth",
    about: "What you own, what you owe, and where both are heading.",
    guide: "networth",
  },
  {
    key: "retirement",
    href: "/app/retirement",
    title: "Retirement",
    about: "What it takes to build a nest egg big enough that you stop needing a paycheque.",
    guide: "retirement",
  },
  {
    key: "priorities",
    href: "/app/priorities",
    title: "Priorities",
    about: "What you're working toward, and when each one lands.",
    guide: "priorities",
  },
  {
    key: "edit-priorities",
    href: "/app/priorities/edit",
    title: "Edit priorities",
    about: "Add, remove, or reorder the things you're working toward.",
  },
  {
    key: "decisions",
    href: "/app/decisions",
    title: "Decision Hub",
    about:
      "Try something before you do it. Every tool runs against your real plan, and saved what-ifs are listed here.",
    guide: "decisions",
  },
  {
    key: "update-data",
    href: "/app/update-data",
    title: "Update data",
    about:
      "Everything your plan is built on. Change anything here and Abi rebuilds the plan around it.",
    guide: "updatedata",
  },
  {
    key: "sharpen",
    href: "/app/sharpen",
    title: "Sharpen your plan",
    about: "A few numbers Abi couldn't get on its own, sorted by how much being wrong would cost.",
  },
  {
    key: "achievements",
    href: "/app/achievements",
    title: "Achievements",
    about:
      "Fills in over time as you use Abi: how you're doing now against what might have happened without it.",
    guide: "achievements",
  },
  {
    key: "settings",
    href: "/app/settings",
    title: "Settings",
    about: "Your sign-in, your employer code, your plan and billing, and your household.",
  },
  ...TOOLS.map((t) => ({
    key: `decisions/${t.slug}`,
    href: t.href,
    title: t.title,
    about: t.description,
  })),
];

export const PAGE_KEYS = APP_PAGES.map((p) => p.key);

export function pageByKey(key: string): AppPage | undefined {
  return APP_PAGES.find((p) => p.key === key);
}

/** The page an app path is (for "the user is on <page>"), longest match first. */
export function pageAt(path: string): AppPage | undefined {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, "") || "/app";
  return [...APP_PAGES]
    .sort((a, b) => b.href.length - a.href.length)
    .find((p) => clean === p.href || clean.startsWith(`${p.href}/`));
}

/** A page's guide as plain text, step by step (site help: what the page shows, in Abi's words). */
export function guideText(page: AppPage): string[] {
  return page.guide ? GUIDES[page.guide].map((s) => s.t) : [];
}
